import FrameworkContractRecapModal from "components/Beneficiar/BeneficiaryFrameworkContracts/RecapModal";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TermTemplate } from "store/termTemplatesStore";

import styles from "./style.module.scss";

type Tab = "framework" | "generic";

interface TemplatesModalProps {
    open: boolean;
    onClose: () => void;
    templates: TermTemplate[] | undefined;
    frameworkContracts?: TermTemplate[];
    onSubmit: (template: TermTemplate) => void;
}

const EyeIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1.5 10s3-6 8.5-6 8.5 6 8.5 6-3 6-8.5 6S1.5 10 1.5 10z" />
        <circle cx="10" cy="10" r="2.5" />
    </svg>
);

const TemplatesModal = ({ open, onClose, templates, frameworkContracts, onSubmit }: TemplatesModalProps) => {
    const [t] = useTranslation();

    const genericList = useMemo(() => templates ?? [], [templates]);
    const frameworkList = useMemo(() => frameworkContracts ?? [], [frameworkContracts]);
    const hasFramework = frameworkList.length > 0;
    const hasGeneric = genericList.length > 0;

    const [tab, setTab] = useState<Tab>(hasFramework ? "framework" : "generic");
    const [selectedId, setSelectedId] = useState<number>(-1);
    const [previewed, setPreviewed] = useState<TermTemplate | undefined>(undefined);

    useEffect(() => {
        if (open) {
            setTab(hasFramework ? "framework" : "generic");
            setSelectedId(-1);
        }
    }, [open, hasFramework]);

    const currentList = tab === "framework" ? frameworkList : genericList;

    const selectedTemplate = useMemo(
        () => [...frameworkList, ...genericList].find((item) => item.id === selectedId),
        [frameworkList, genericList, selectedId],
    );

    const handleSubmit = () => {
        if (selectedTemplate) onSubmit(selectedTemplate);
    };

    return (
        <>
            <ModalBase
                open={open}
                onClose={onClose}
                modalTitle={t("TermTemplates.ChooseTheTypology")}
                widthTransition={{ width: "500px", active: true }}
                contentStyle={{ padding: 0 }}
            >
                <div>
                    <div className={styles.body}>
                        {hasFramework && hasGeneric && (
                            <div className={styles.tabs} role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === "framework"}
                                    className={`${styles.tab} ${tab === "framework" ? styles.tabOn : ""}`}
                                    onClick={() => setTab("framework")}
                                >
                                    {t("TermTemplates.FrameworkContractsTab")}
                                    <span className={styles.count}>{frameworkList.length}</span>
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === "generic"}
                                    className={`${styles.tab} ${tab === "generic" ? styles.tabOn : ""}`}
                                    onClick={() => setTab("generic")}
                                >
                                    {t("TermTemplates.GenericTypologiesTab")}
                                    <span className={styles.count}>{genericList.length}</span>
                                </button>
                            </div>
                        )}

                        <div className={styles.list}>
                            {currentList.length === 0 && (
                                <div className={styles.empty}>{t("TermTemplates.NoTypologyAvailable")}</div>
                            )}
                            {currentList.map((template) => {
                                const isSelected = template.id === selectedId;
                                return (
                                    <div
                                        key={template.id}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedId(template.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") setSelectedId(template.id);
                                        }}
                                        className={`${styles.row} ${isSelected ? styles.rowSel : ""}`}
                                    >
                                        <span className={styles.radio}>
                                            <span className={styles.radioInner} />
                                        </span>
                                        <span className={styles.rowName}>{template.name}</span>
                                        <button
                                            type="button"
                                            title={t("TermTemplates.ViewDetail") as string}
                                            aria-label={t("TermTemplates.ViewDetail") as string}
                                            className={styles.eye}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewed(template);
                                            }}
                                        >
                                            <EyeIcon />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <StyledButton
                            buttonType="primary"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={!selectedTemplate}
                        >
                            {t("TermTemplates.UseTypology")}
                        </StyledButton>
                    </div>
                </div>
            </ModalBase>

            <FrameworkContractRecapModal
                open={!!previewed}
                template={previewed}
                onClose={() => setPreviewed(undefined)}
            />
        </>
    );
};

export default TemplatesModal;
